import { inject, Injectable } from '@angular/core';
import { HsBlazorService } from '@hsetu/core-services';
import {
  HsBookingDTO,
  HsBookingRepository,
  HsFinancialRepository,
  HsPaymentDTO,
  HsPaymentRepository,
} from '@hsetu/ngui-modules-financial';

const invoiceServiceKey = 'kfmInvoice';
const bookingServiceKey = 'financialBooking';
const paymentServiceKey = 'financialPayment';

/** WASM liefert Datumsfelder als ISO-Strings — in Date-Objekte konvertieren */
function toDate(value: unknown): Date | undefined {
  if (!value) return undefined;
  const d = new Date(value as string);
  return isNaN(d.getTime()) ? undefined : d;
}

function parseAmount(value: unknown): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(String(value).replace(',', '.'));
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Strips legacy "CustomerJSON — " prefix from old bookings so the
 * Buchungstext column shows only the actual booking description.
 */
function extractBookingText(bodyText: unknown): string {
  if (!bodyText) return '';
  const text = String(bodyText);
  const idx = text.indexOf(' — ');
  return idx !== -1 ? text.substring(idx + 3) : text;
}

/**
 * Formats a ContactDTO (nested in the Invoice from GetInvoicesByIds) as
 * "Anrede Vorname Nachname (Kundennummer)".
 * Falls back to companyName when person-name fields are empty.
 * Kundennummer comes from the CustomerReference registration (RegistrationType = 5).
 */
function formatContactName(contact: Record<string, unknown>): string {
  const nameParts = [
    contact['salutation'],
    contact['firstName'],
    contact['lastName'],
  ].filter((p): p is string => typeof p === 'string' && p.length > 0);
  const name =
    nameParts.length > 0
      ? nameParts.join(' ')
      : String(contact['companyName'] ?? '');

  // Kundennummer: CustomerReference-Registration (RegistrationType.CustomerReference = 5)
  const registrations = contact['registrations'] as
    | Array<Record<string, unknown>>
    | undefined;
  const customerRefReg = registrations?.find(
    (r) => (r['registrationType'] as number) === 5,
  );
  const customerNumber = String(customerRefReg?.['registrationNumber'] ?? '');

  return customerNumber ? `${name} (${customerNumber})` : name;
}

function normalizeBooking(
  raw: Record<string, unknown>,
  paymentSum = 0,
  customer = '',
): HsBookingDTO {
  // WASM returns transactionOutgoing/transactionIncoming as Soll/Haben account identifiers
  // (not amounts). Gross and payments must come from price sets and real Payment entities.

  // Bookings created by BookInvoiceHandler store amounts in VAT price sets
  // (fullSet/reducedSet/zeroSet).
  const fullSet = raw['fullSet'] as Record<string, unknown> | undefined;
  const reducedSet = raw['reducedSet'] as Record<string, unknown> | undefined;
  const zeroSet = raw['zeroSet'] as Record<string, unknown> | undefined;
  const gross =
    parseAmount(fullSet?.['grossTotal']) +
    parseAmount(reducedSet?.['grossTotal']) +
    parseAmount(zeroSet?.['grossTotal']);

  const payments = paymentSum;
  const isStorno = (raw['bookingType'] as number) === 2; // HsBookingType.Storno

  return {
    ...(raw as unknown as HsBookingDTO),
    // C# serialises ParentBookingIdentifier as 'parentBookingUId' — map explicitly
    parentBookingIdentifier: raw['parentBookingUId'] as string | undefined,

    documentIdentifier: raw['documentUId'] as string | undefined,
    gross,
    payments: isStorno ? 0 : payments,
    outstanding: isStorno ? 0 : Math.max(0, gross - payments),
    // Strip legacy "CustomerJSON — " prefix so bookingText shows only the text part
    bodyText: extractBookingText(raw['bodyText']),
    // Customer resolved from the linked invoice's Contact — see buildCustomerMap()
    customer,
    customerNumber: String(raw['customerNumber'] ?? ''),
    // transactionOutgoing/Incoming are Soll/Haben account identifiers — preserve them as-is
    documentDate: toDate(raw['documentDate']),
    dueDate: toDate(raw['dueDate']),
    bookingDate: toDate(raw['bookingDate']),
  };
}

function normalizePayment(raw: Record<string, unknown>): HsPaymentDTO {
  return {
    ...(raw as unknown as HsPaymentDTO),
    paymentDate: toDate(raw['paymentDate']),
  };
}

@Injectable({ providedIn: 'root' })
class KfmBookingRepository implements HsBookingRepository {
  private readonly _blazor = inject(HsBlazorService);

  /**
   * Loads invoices by their document IDs in a single batch call and returns
   * a Map of invoice uId → formatted customer display string.
   *
   * GetInvoicesByIds (via SalesLoader) already populates the nested customer
   * ContactDTO, so no separate contact lookup is needed.
   */
  private async buildCustomerMap(
    documentIds: string[],
  ): Promise<Map<string, string>> {
    const map = new Map<string, string>();
    if (documentIds.length === 0) return map;

    const result = await this._blazor.invokeService(
      invoiceServiceKey,
      'GetByIds',
      { ids: documentIds },
    );

    const invoices =
      (result as Record<string, unknown[]> | null)?.['invoices'] ?? [];
    for (const inv of invoices) {
      const invoice = inv as Record<string, unknown>;
      const docId = String(invoice['uId'] ?? '');
      const contact = invoice['customer'] as
        | Record<string, unknown>
        | undefined;
      if (docId && contact) {
        map.set(docId, formatContactName(contact));
      }
    }
    return map;
  }

  async getBookings(): Promise<HsBookingDTO[]> {
    const result = await this._blazor.invokeService(bookingServiceKey, 'Get', {
      pageIndex: 0,
      pageSize: 2000,
      sortBy: null,
      filterBy: null,
    });

    const raw = result as { data: Record<string, unknown>[] };
    const bookings = raw?.data ?? [];

    // One batch call to resolve all customer names at once
    const documentIds = bookings
      .map((b) => b['documentUId'])
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
    const customerMap = await this.buildCustomerMap(documentIds);

    return Promise.all(
      bookings.map(async (booking) => {
        const bookingId = String(booking['uId'] ?? '');
        let paymentSum = 0;

        if (bookingId) {
          const payResult = await this._blazor.invokeService(
            paymentServiceKey,
            'Get',
            {
              pageIndex: 0,
              pageSize: 200,
              sortBy: null,
              filterBy: {
                filterStatement: 'return i.bookingIdentifier === p[0]',
                parameters: [bookingId],
              },
            },
          );
          const payments =
            (payResult as { data: Record<string, unknown>[] })?.data ?? [];
          paymentSum = payments.reduce((sum, p) => {
            const amount = parseAmount(p['price']);
            return (p['paymentType'] as number) === 5
              ? sum - amount
              : sum + amount;
          }, 0);
        }

        const docId = String(booking['documentUId'] ?? '');
        const customer = customerMap.get(docId) ?? '';

        return normalizeBooking(booking, paymentSum, customer);
      }),
    );
  }

  async getBookingById(id: string): Promise<HsBookingDTO> {
    const result = await this._blazor.invokeService(bookingServiceKey, 'Get', {
      pageIndex: 0,
      pageSize: 2000,
      sortBy: null,
      filterBy: {
        filterStatement: 'return i.uId === p[0]',
        parameters: [id],
      },
    });

    const raw = result as { data: Record<string, unknown>[] };
    const booking = (raw?.data ?? [])[0] ?? {};

    const docId = String(booking['documentUId'] ?? '');
    const customerMap = await this.buildCustomerMap(docId ? [docId] : []);
    const customer = customerMap.get(docId) ?? '';

    return normalizeBooking(booking, 0, customer);
  }
}

@Injectable({ providedIn: 'root' })
class KfmPaymentRepository implements HsPaymentRepository {
  private readonly _blazor = inject(HsBlazorService);

  async getPaymentsForBooking(bookingUId: string): Promise<HsPaymentDTO[]> {
    const result = await this._blazor.invokeService(paymentServiceKey, 'Get', {
      pageIndex: 0,
      pageSize: 200,
      sortBy: null,
      filterBy: {
        filterStatement: 'return i.bookingIdentifier === p[0]',
        parameters: [bookingUId],
      },
    });

    const raw = result as { data: Record<string, unknown>[] };
    return (raw?.data ?? []).map(normalizePayment);
  }

  async getPaymentById(id: string): Promise<HsPaymentDTO> {
    const result = await this._blazor.invokeService(paymentServiceKey, 'Get', {
      pageIndex: 0,
      pageSize: 200,
      sortBy: null,
      filterBy: {
        filterStatement: 'return i.uId === p[0]',
        parameters: [id],
      },
    });

    const raw = result as { data: Record<string, unknown>[] };
    return normalizePayment((raw?.data ?? [])[0]);
  }
}

@Injectable({ providedIn: 'root' })
export class KfmFinancialRepository implements HsFinancialRepository {
  readonly bookingRepository = inject(KfmBookingRepository);
  readonly paymentRepository = inject(KfmPaymentRepository);
}
